import React, { useEffect, useState } from 'react';
import api from '../api/axios';

// (F) Mijoz to'lovlari: kiritish (FIFO standart), qo'lda taqsimlash, bekor qilish
// (sababi bilan), balans. Backend: app/routers/payments.py - faqat buxgalteriya /
// rahbariyat. Summalar backend'dan satr ("5000000.00", Decimal) - bu yerda
// faqat ko'rsatiladi, hisob-kitob serverda.
interface Allocation { invoice_id: number; invoice_number: string; amount: string; }
interface Payment {
  id: number; client_id: number; contract_id?: number | null; currency: string; amount: string; payment_date: string;
  method: 'bank' | 'cash'; document_number?: string | null; note?: string | null; status: 'active' | 'cancelled';
  allocated: string; unallocated: string; allocations: Allocation[];
  cancelled_at?: string | null; cancel_reason?: string | null;
}
interface ClientInvoice { id: number; number: string; kind: string; currency: string; amount: string; paid: string; outstanding: string; payment_state: string; due_date?: string | null; overdue?: boolean; }
type Balance = Record<string, { invoiced: string; paid: string; outstanding: string; unpaid_advance: string; credit: string; debt: string; credit_unbound?: string }>;

const fmt = (s?: string | null) => Number(s || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const PAYMENT_STATE: Record<string, { label: string; color: string }> = {
  unpaid: { label: "To'lanmagan", color: 'bg-red-100 text-red-700' },
  partial: { label: 'Qisman', color: 'bg-yellow-100 text-yellow-800' },
  paid: { label: "To'langan", color: 'bg-green-100 text-green-700' },
};
// Mahalliy sana (YYYY-MM-DD). toISOString() UTC beradi - Toshkentda 00:00-05:00 oralig'ida
// kechagi sana chiqib, to'lov sanasi chegarasi (max) bugunni taqiqlab qo'yardi.
export const localToday = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const today = localToday;
// UI sanalari dd.mm.yyyy (H6); kiritishda dd.mm.yyyy yoki yyyy-mm-dd qabul qilinadi.
export const fmtDate = (iso?: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '');
export const parseDate = (text: string): string | null => {
  const t = text.trim();
  let m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? t : null;
};

const PaymentsPanel: React.FC<{ clientId: number; contractId?: number; currency?: string; canFull: boolean; showInvoices?: boolean;
  onChange?: () => void; }> = ({ clientId, contractId, currency = 'uzs', canFull, showInvoices, onChange }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [balance, setBalance] = useState<Balance>({});
  const [form, setForm] = useState({ amount: '', payment_date: today(), method: 'bank', document_number: '', note: '', currency });
  const [editing, setEditing] = useState<{ id: number; rows: { invoice_id: number; amount: string }[] } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = contractId ? { contract_id: contractId } : { client_id: clientId };
      const [p, b, i] = await Promise.all([
        api.get('/payments', { params }), api.get('/payments/balance', { params }),
        api.get('/payments/invoices', { params: { client_id: clientId } }),
      ]);
      setPayments(p.data); setBalance(b.data); setInvoices(i.data);
    } catch { setPayments([]); setBalance({}); setInvoices([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId, contractId]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await fn(); await load(); onChange?.(); } catch (e: any) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Xato - summa/sana to\'g\'riligini tekshiring');
    } finally { setBusy(false); }
  };

  const create = () => {
    if (!form.amount.trim()) { setError('Summa kiriting'); return; }
    run(async () => {
      await api.post('/payments', { client_id: clientId, contract_id: contractId ?? null, currency: form.currency, amount: form.amount.trim(),
        payment_date: form.payment_date, method: form.method, document_number: form.document_number || null, note: form.note || null });
      setForm({ amount: '', payment_date: today(), method: 'bank', document_number: '', note: '', currency: form.currency });
    });
  };
  const cancel = (p: Payment) => {
    const reason = window.prompt(`To'lov ${fmt(p.amount)} (${p.document_number || fmtDate(p.payment_date)}) - bekor qilish sababi (majburiy):`);
    if (reason === null) return;
    run(() => api.post(`/payments/${p.id}/cancel`, { reason }));
  };
  const saveAllocations = () => editing && run(async () => {
    await api.put(`/payments/${editing.id}/allocations`, {
      allocations: editing.rows.filter(r => r.invoice_id && r.amount.trim()).map(r => ({ invoice_id: r.invoice_id, amount: r.amount.trim() })),
    });
    setEditing(null);
  });

  return (
    <div className="mt-3 border-t pt-3">
      <h5 className="font-semibold text-gray-700 text-sm mb-2">💳 To&apos;lovlar</h5>
      {Object.entries(balance).map(([cur, b]) => (
        <div key={cur} className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs mb-2">
          <div className="rounded p-2 font-semibold uppercase">{cur}</div>
          <div className="bg-gray-50 rounded p-2">Hisoblar: <b>{fmt(b.invoiced)}</b></div>
          <div className="bg-gray-50 rounded p-2">To&apos;langan: <b>{fmt(b.paid)}</b></div>
          <div className="bg-red-50 rounded p-2">Qarz: <b>{fmt(b.debt)}</b></div>
          <div className="bg-orange-50 rounded p-2">To&apos;lanmagan avans: <b>{fmt(b.unpaid_advance)}</b></div>
          <div className="bg-green-50 rounded p-2" title={contractId ? "Shu shartnomaga bog'langan - faqat shu shartnomaning keyingi hisobiga avtomatik o'tadi" : "Shartnomali to'lov qoldig'i o'z shartnomasida qoladi; boshqa shartnomaga - faqat qo'lda taqsimlash (tarix saqlanadi)"}>
            Ortiqcha to&apos;lov{contractId ? ' (shu shartnoma)' : ''}: <b>{fmt(b.credit)}</b>
            {!contractId && Number(b.credit_unbound || 0) > 0 && <span className="text-gray-500"> (shartnomasiz: {fmt(b.credit_unbound)})</span>}
          </div>
        </div>
      ))}
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {canFull && (
        <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
          <input value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Summa" inputMode="decimal"
            className="border rounded px-2 py-1 w-32" />
          <input type="date" value={form.payment_date} max={today()} onChange={e => setForm({ ...form, payment_date: e.target.value })} className="border rounded px-2 py-1" />
          {!contractId && (
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="border rounded px-2 py-1">
              <option value="uzs">UZS</option><option value="usd">USD</option><option value="eur">EUR</option>
            </select>
          )}
          <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} className="border rounded px-2 py-1">
            <option value="bank">Bank</option><option value="cash">Naqd</option>
          </select>
          <input value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })}
            placeholder="To'lov topshiriqnomasi №" className="border rounded px-2 py-1 w-44" />
          <button disabled={busy} onClick={create} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            + To&apos;lov kiritish
          </button>
          <span className="text-gray-400">{contractId
            ? "Standart: shu shartnomaning eng eski to'lanmagan hisobidan; ortig'i shu shartnomada qoladi (boshqa shartnomaga - faqat qo'lda taqsimlash)."
            : "Standart: mijozning eng eski to'lanmagan hisobidan; ortig'i mijoz balansida qoladi."}</span>
        </div>
      )}

      {showInvoices && invoices.length > 0 && (
        <table className="w-full text-xs mb-2">
          <thead><tr className="text-left text-gray-500 border-b"><th className="py-1">Hisob</th><th>Tur</th><th className="text-right">Summa</th>
            <th className="text-right">To&apos;langan</th><th className="text-right">Qoldiq</th><th>Holat</th></tr></thead>
          <tbody>{invoices.map(i => (
            <tr key={i.id} className="border-b"><td className="py-1">{i.number}</td><td>{i.kind === 'advance' ? 'Avans' : 'Yakuniy'}</td>
              <td className="text-right">{fmt(i.amount)}</td><td className="text-right">{fmt(i.paid)}</td><td className="text-right">{fmt(i.outstanding)}</td>
              <td><span className={`px-2 py-0.5 rounded ${PAYMENT_STATE[i.payment_state]?.color || ''}`}>{PAYMENT_STATE[i.payment_state]?.label}</span>
                {i.overdue && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={`Muddat: ${fmtDate(i.due_date)}`}>muddati o&apos;tgan</span>}</td></tr>
          ))}</tbody>
        </table>
      )}

      {payments.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-gray-500 border-b"><th className="py-1">Sana</th><th>Hujjat</th><th>Usul</th>
            <th className="text-right">Summa</th><th>Taqsimot</th><th></th></tr></thead>
          <tbody>{payments.map(p => (
            <tr key={p.id} className={`border-b align-top ${p.status === 'cancelled' ? 'text-gray-400 line-through' : ''}`}>
              <td className="py-1 whitespace-nowrap">{fmtDate(p.payment_date)}</td><td>{p.document_number || '—'}</td><td>{p.method === 'bank' ? 'Bank' : 'Naqd'}</td>
              <td className="text-right">{fmt(p.amount)}</td>
              <td>
                {p.allocations.map(a => `${a.invoice_number}: ${fmt(a.amount)}`).join(', ') || '—'}
                {p.status === 'active' && Number(p.unallocated) > 0 && <span className="text-green-700"> · ortiqcha {fmt(p.unallocated)}</span>}
                {p.status === 'cancelled' && <div className="no-underline">Bekor: {p.cancel_reason}</div>}
                {editing?.id === p.id && (
                  <div className="mt-1 space-y-1 no-underline">
                    {editing.rows.map((r, idx) => (
                      <div key={idx} className="flex gap-1">
                        <select value={r.invoice_id} onChange={e => setEditing({ ...editing, rows: editing.rows.map((x, j) => j === idx ? { ...x, invoice_id: Number(e.target.value) } : x) })}
                          className="border rounded px-1">
                          <option value={0}>—</option>
                          {invoices.filter(i => i.currency === p.currency).map(i => (
                            <option key={i.id} value={i.id}>{i.number} (qoldiq {fmt(i.outstanding)})</option>
                          ))}
                        </select>
                        <input value={r.amount} onChange={e => setEditing({ ...editing, rows: editing.rows.map((x, j) => j === idx ? { ...x, amount: e.target.value } : x) })}
                          className="border rounded px-1 w-28" inputMode="decimal" />
                      </div>
                    ))}
                    <div className="flex gap-1">
                      <button onClick={() => setEditing({ ...editing, rows: [...editing.rows, { invoice_id: 0, amount: '' }] })} className="px-2 border rounded">+ qator</button>
                      <button disabled={busy} onClick={saveAllocations} className="px-2 bg-blue-600 text-white rounded">Saqlash</button>
                      <button onClick={() => setEditing(null)} className="px-2 border rounded">Bekor</button>
                    </div>
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap space-x-1 no-underline">
                {canFull && p.status === 'active' && editing?.id !== p.id && (
                  <button onClick={() => setEditing({ id: p.id, rows: p.allocations.map(a => ({ invoice_id: a.invoice_id, amount: a.amount })) })}
                    className="px-2 py-0.5 border rounded hover:bg-gray-50">Taqsimlash</button>
                )}
                {canFull && p.status === 'active' && (
                  <button disabled={busy} onClick={() => cancel(p)} className="px-2 py-0.5 border rounded text-red-700 hover:bg-red-50">Bekor</button>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
};

export default PaymentsPanel;
